<template>
  <ul class="view-tab">
    <li
      @click="() => changeTabValue(ViewTabOption.APPS)"
      :class="[isActive(ViewTabOption.APPS) && 'view-tab__item--active', 'view-tab__item']"
    >
      <div v-if="appContainsErrors" class="view-tab__error"></div>
      <button class="view-tab__button">{{ t("apps") }} ({{ totalApps }})</button>
    </li>

    <li
      @click="() => changeTabValue(ViewTabOption.SERVICES)"
      :class="[isActive(ViewTabOption.SERVICES) && 'view-tab__item--active', 'view-tab__item']"
    >
      <div v-if="serviceContainsErrors" class="view-tab__error"></div>
      <button class="view-tab__button">{{ t("services") }} ({{ totalServices }})</button>
    </li>
  </ul>
</template>

<script lang="ts">
import Utils from "@/utils/utils"
import { defineComponent, computed, watch, PropType, ref } from "@vue/composition-api"
import { IWorkspaceSchema, WorkspaceTemplate } from "@/types/workspace"
import { createTranslationModule } from "@/plugins/i18n"
import { ViewTabOption } from "../../lib/types"
import { useWizard } from "@/composable/useWizard"
import { WizardConfigItem } from "@/types/wizard"
import { QUERY_VIEW_TYPE } from "@/config/constants"
import router, { replace } from "@/router"
import "../../scss/_viewTab.scss"

export default defineComponent({
  name: "ViewTab",
  props: {
    value: {
      type: String as PropType<ViewTabOption>,
      required: true
    },
    workspace: {
      type: Object as PropType<WorkspaceTemplate>,
      required: true
    }
  },
  setup(props, ctx) {
    const activeTab = Utils.vModel<ViewTabOption>(props, ctx.emit)
    const t = createTranslationModule("WorkspaceEdit.ViewTabs")
    const renderKey = ref(0)

    const _routerQuery = computed(() => {
      const query = router.currentRoute.query

      if (QUERY_VIEW_TYPE in query) {
        return query[QUERY_VIEW_TYPE] as ViewTabOption
      }

      return null
    })

    const space = computed(() => {
      return props.workspace
    })

    const appContainsErrors = computed(() => {
      const schema = props.workspace.schema as IWorkspaceSchema

      const schemaUserConfiguration = computed(() => {
        return schema.assets.reduce((collection: WizardConfigItem[], a) => {
          if (a.userConfigurations) collection.push(...a.userConfigurations)
          return collection
        }, [])
      })

      const { validate } = useWizard(schemaUserConfiguration)

      return schema.assets.some(a => {
        if (a.userConfigurations) {
          return !a.userConfigurations.every(uc => validate(uc.resourceKey))
        } else {
          return false
        }
      })
    })

    const serviceContainsErrors = computed(() => {
      const schema = props.workspace.schema as IWorkspaceSchema

      const schemaUserConfiguration = computed(() => {
        return schema.services.reduce((collection: WizardConfigItem[], a) => {
          if (a.userConfigurations) collection.push(...a.userConfigurations)
          return collection
        }, [])
      })

      const { validate } = useWizard(schemaUserConfiguration)

      return schema.services.some(a => {
        if (a.userConfigurations) {
          return !a.userConfigurations.every(uc => validate(uc.resourceKey))
        } else {
          return false
        }
      })
    })

    const totalApps = computed(() => {
      const assetsDeployed = props.workspace.schemaDeployed?.assets
      const assets = props.workspace.schema.assets

      if (assetsDeployed && renderKey.value > -1) {
        const mergedAssets = [...assetsDeployed, ...assets]

        mergedAssets.reduce((collection: string[], asset) => {
          if (!collection.includes(asset.id)) {
            collection.push(asset.id)
          }
          return collection
        }, []).length
      }

      return assets.length ?? 0
    })

    const totalServices = computed(() => {
      const services = props.workspace.schema.services
      const servicesDeployed = props.workspace.schemaDeployed?.services

      if (servicesDeployed && renderKey.value > -1) {
        const mergedAssets = [...servicesDeployed, ...services]

        mergedAssets.reduce((collection: string[], asset) => {
          if (!collection.includes(asset.id)) {
            collection.push(asset.id)
          }
          return collection
        }, []).length
      }

      return services?.length ?? 0
    })

    const changeTabValue = (value: ViewTabOption) => {
      if (activeTab.value !== value) {
        activeTab.value = value

        replace({
          query: {
            [`${QUERY_VIEW_TYPE}`]: value
          }
        })
      }
    }

    const isActive = (value: ViewTabOption) => {
      return activeTab.value === value
    }

    const _fixTabBasedOnQuery = () => {
      if (_routerQuery.value) {
        changeTabValue(_routerQuery.value)
      }
    }

    watch(_routerQuery, (queryValue: ViewTabOption | null) => {
      if (queryValue) changeTabValue(queryValue as ViewTabOption)
    })

    watch(space, () => {
      renderKey.value++
    })

    _fixTabBasedOnQuery()

    return {
      t,
      serviceContainsErrors,
      appContainsErrors,
      ViewTabOption,
      totalServices,
      changeTabValue,
      activeTab,
      totalApps,
      isActive
    }
  }
})
</script>
